import { Box, Button, Grid, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DynamicFormRef } from '@/components/dynamicFormRef'
import { DinamicFormRefValues, DynamicRefRef } from '@/components/dynamicFormRef/types'
import { evidenceTemplate } from '@/components/evidences/utils/constants'
import { EvidenceData } from '@/hooks/evidences/typyng'
import { evidenceDefaultValues, useDrawerInputs } from '@/hooks/evidences/useEvidences'
import { useRefs } from '@/hooks/useReactHookModified'
import { EvidencesTypeEnum } from '@/utils/enums/evidences'
import ConfigLayout from '../components/configLayout'
import { useStyles } from './configureSolverEvidences.styles'
import { ConfigureSolverEvidencesProps } from '../types'

const SOLVER_INDEX = 1

const ConfigureSolverEvidences = ({ data, onSave, onBack }: ConfigureSolverEvidencesProps) => {
  const { t } = useTranslation()
  const styles = useStyles()
  const { model } = useDrawerInputs()
  const refs = useRefs<DynamicRefRef>()

  const [localData, setLocalData] = useState<EvidenceData[]>(data.length ? data : [{}])
  const [expand, setExpand] = useState<Record<string, boolean>>({})
  const [existCheck, setExistCheck] = useState<Record<string, boolean>>({})
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const currentData = useMemo(() => localData[0] ?? {}, [localData])

  useEffect(() => {
    const newExpand: Record<string, boolean> = {}
    const newExistCheck: Record<string, boolean> = {}
    const item = localData[0] ?? {}

    Object.entries(item).forEach(([key, value]) => {
      if (value?.active) {
        newExpand[key] = true
        const checkFields = Object.keys(model[key]?.check ?? {})
        if (checkFields.length > 0) {
          newExistCheck[key] = !checkFields.some((field) => !!value[field])
        }
      }
    })

    setExpand(newExpand)
    setExistCheck(newExistCheck)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  const hasSomeActive = useMemo(() => {
    return Object.values(currentData).some((item) => item?.active)
  }, [currentData])

  const hasErrors = useMemo(() => {
    if (!hasSubmitted) return false

    let errorFound = false
    Object.entries(expand).forEach(([key, isExpanded]) => {
      if (!isExpanded) return

      const headerRef = refs.refsByKey?.[`${key}-header-${SOLVER_INDEX}`]
      const checkRef = refs.refsByKey?.[`${key}-check-${SOLVER_INDEX}`]
      const inputRef = refs.refsByKey?.[`${key}-input-${SOLVER_INDEX}`]

      if (Object.values(headerRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (Object.values(checkRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (Object.values(inputRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (existCheck[key]) errorFound = true
    })

    return errorFound || !hasSomeActive
  }, [expand, existCheck, hasSubmitted, hasSomeActive, refs.refsByKey])

  const addData = useCallback(
    ({
      key,
      name,
      value,
    }: {
      key: string
      name: string
      value: DinamicFormRefValues
      oldValue: DinamicFormRefValues
    }) => {
      const dataCopy = structuredClone(localData)
      const existCheckCopy = structuredClone(existCheck)

      if (name === 'active') {
        const expandCopy = structuredClone(expand)
        const defaultValues = evidenceDefaultValues({ active: !!value })
        const newValues = { ...defaultValues?.[key as EvidencesTypeEnum] }

        refs.refsByKey?.[`${key}-header-${SOLVER_INDEX}`]?.setValues({ name: 'required', value: true })
        refs.refsByKey?.[`${key}-check-${SOLVER_INDEX}`]?.reset(newValues)
        refs.refsByKey?.[`${key}-input-${SOLVER_INDEX}`]?.reset(newValues)

        if (!!value) {
          dataCopy[0] = {
            ...dataCopy[0] ?? {},
            [key]: { ...dataCopy[0]?.[key] ?? {}, ...newValues },
          }
        } else {
          dataCopy[0] = { ...dataCopy[0] ?? {}, [key]: undefined }
          existCheckCopy[key] = false
          setExistCheck(existCheckCopy)
        }

        setLocalData(dataCopy)
        expandCopy[key] = !!value
        setExpand(expandCopy)
      } else {
        const check = Object.values(model[key]?.check ?? {})

        if (!!check?.length) {
          const newData = {
            ...dataCopy[0],
            [key]: { ...dataCopy[0]?.[key] ?? {}, [name]: value },
          }
          const hasNoCheck = !Object.values(model[key]?.check ?? {})?.some(
            (item) => !!newData?.[key]?.[item?.name ?? '']
          )
          existCheckCopy[key] = hasNoCheck
          setExistCheck(existCheckCopy)
        }

        dataCopy[0] = {
          ...dataCopy[0] ?? {},
          [key]: { ...dataCopy[0]?.[key] ?? {}, [name]: value },
        }
        setLocalData(dataCopy)
      }
    },
    [localData, expand, existCheck, model, refs.refsByKey]
  )

  const validateData = useCallback(
    ({
      key,
      name,
      value,
    }: {
      key: string
      name: string
      value: DinamicFormRefValues
      oldValue: DinamicFormRefValues
    }) => {
      const setValues = refs.refsByKey[`${key}-input-${SOLVER_INDEX}`]?.setValues
      const cleanError = refs.refsByKey[`${key}-input-${SOLVER_INDEX}`]?.cleanError
      const dataCopy = structuredClone(localData)

      if (cleanError) {
        cleanError(name)
      }

      if (['minAmount', 'maxAmount'].includes(name)) {
        const datainput = dataCopy[0]?.[key]
        const minAmount = name === 'minAmount' ? value : datainput?.minAmount
        const maxAmount = name === 'maxAmount' ? value : datainput?.maxAmount

        if (!value || Number(minAmount ?? 1) > Number(maxAmount ?? 1)) {
          let newValue = name === 'minAmount' ? maxAmount : minAmount
          if (!value && name === 'minAmount') {
            newValue = '1'
          }
          if (setValues) {
            setValues({ name, value: (newValue as string) || '1' })
          }
          dataCopy[0] = {
            ...dataCopy[0],
            [key]: { ...dataCopy[0]?.[key] ?? {}, [name]: newValue || '1' },
          }
        } else {
          dataCopy[0] = {
            ...dataCopy[0],
            [key]: { ...dataCopy[0]?.[key] ?? {}, [name]: value },
          }
        }
        setLocalData(dataCopy)
      } else if (name === 'maxCharacters' && !value) {
        if (setValues) {
          setValues({ name, value: '1' })
          dataCopy[0] = {
            ...dataCopy[0],
            [key]: { ...dataCopy[0]?.[key] ?? {}, [name]: '1' },
          }
          setLocalData(dataCopy)
        }
      }
    },
    [localData, refs.refsByKey]
  )

  const handleUseDefault = () => {
    const template = evidenceTemplate
    setLocalData([structuredClone(template)])

    const newExpand = Object.keys(template).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    )
    setExpand(newExpand)
    setExistCheck({})

    Object.entries(model).forEach(([key]) => {
      const value = template[key as keyof typeof template]
      refs.refsByKey[`${key}-header-${SOLVER_INDEX}`]?.reset(value)
      refs.refsByKey[`${key}-check-${SOLVER_INDEX}`]?.reset(value)
      refs.refsByKey[`${key}-input-${SOLVER_INDEX}`]?.reset(value)
    })
  }

  const handleSave = () => {
    setHasSubmitted(true)

    if (!hasSomeActive) return

    let errorFound = false
    Object.entries(expand).forEach(([key, isExpanded]) => {
      if (!isExpanded) return

      const headerRef = refs.refsByKey?.[`${key}-header-${SOLVER_INDEX}`]
      const checkRef = refs.refsByKey?.[`${key}-check-${SOLVER_INDEX}`]
      const inputRef = refs.refsByKey?.[`${key}-input-${SOLVER_INDEX}`]

      if (Object.values(headerRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (Object.values(checkRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (Object.values(inputRef?.getErrors() ?? {}).length > 0) errorFound = true
      if (existCheck[key]) errorFound = true
    })

    if (errorFound) return

    onSave(localData)
  }

  const evidenceKeys = useMemo(() => Object.keys(model), [model])

  return (
    <ConfigLayout
      title={t('actionPlan.manualCreation.solverEvidences.configureTitle')}
      subtitle={t('actionPlan.manualCreation.solverEvidences.configureSubtitle')}
      onBack={onBack}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t('actionPlan.manualCreation.solverEvidences.saveAndBack')}
      backLabel={t('actionPlan.manualCreation.solverEvidences.back')}
    >
      <Box sx={styles.content}>
        <Box sx={styles.container}>
          <Grid container spacing={2}>
            {evidenceKeys.map((key) => {
              const value = model[key]
              const isExpanded = !!expand[key]
              const hasCheckError = hasSubmitted && existCheck[key]
              
              return (
                <Grid item xs={12} md={6} key={key}>
                  <Box sx={[styles.card, isExpanded && styles.cardActive]}>
                    <DynamicFormRef
                      ref={(element) => {
                        refs.setRef(element, `${key}-header-${SOLVER_INDEX}`)
                        return refs.refsByKey[`${key}-header-${SOLVER_INDEX}`]
                      }}
                      styleSx={{ width: '100%' }}
                      removeForm
                      onChange={(payload) => {
                        const name = payload?.current as string
                        addData({
                          key,
                          name,
                          value: payload?.[name] as DinamicFormRefValues,
                          oldValue: payload?.oldValue ?? '',
                        })
                      }}
                      defaultValues={currentData?.[key]}
                      model={{
                        ...value?.header,
                        required: isExpanded ? value?.header?.required : null,
                      }}
                      parentWrapper={({ children }) => (
                        <Grid
                          sx={styles.cardHeader}
                          container
                          justifyContent='space-between'
                          alignItems='center'
                          children={children}
                          />
                      )}
                    />

                    {isExpanded && (
                      <Box sx={styles.cardBody}>
                        {!!value?.check && (
                          <>
                            {hasCheckError && (
                              <Typography sx={styles.checkError}>
                                <span>*</span> {t('general.validations.selectACheck')}
                              </Typography>
                            )}
                            <DynamicFormRef
                              ref={(element) => {
                                refs.setRef(element, `${key}-check-${SOLVER_INDEX}`)
                                return refs.refsByKey[`${key}-check-${SOLVER_INDEX}`]
                              }}
                              onChange={(payload) => {
                                const name = payload?.current as string
                                addData({
                                  key,
                                  name,
                                  value: payload?.[name] as DinamicFormRefValues,
                                  oldValue: payload?.oldValue ?? '',
                                })
                              }}
                              removeForm
                              model={value?.check}
                              defaultValues={currentData?.[key]}
                              parentWrapper={({ children }) => (
                                <Grid
                                  sx={styles.checksRow}
                                  container
                                  alignItems='center'
                                  children={children}
                                />
                              )}
                            />
                          </>
                        )}

                        <DynamicFormRef
                          ref={(element) => {
                            refs.setRef(element, `${key}-input-${SOLVER_INDEX}`)
                            return refs.refsByKey[`${key}-input-${SOLVER_INDEX}`]
                          }}
                          onChange={(payload) => {
                            const name = payload?.current as string
                            addData({
                              key,
                              name,
                              value: payload?.[name] as DinamicFormRefValues,
                              oldValue: payload?.oldValue ?? '',
                            })
                          }}
                          onBlur={(payload) => {
                            const name = payload?.current as string
                            validateData({
                              key,
                              name,
                              value: payload?.[name] as DinamicFormRefValues,
                              oldValue: payload?.oldValue ?? '',
                            })
                          }}
                          removeForm
                          model={value?.input}
                          defaultValues={currentData?.[key]}
                          parentWrapper={({ children }) => (
                            <Grid
                              sx={styles.inputsRow}
                              container
                              spacing={2}
                              alignItems='center'
                              children={children}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </Box>

        {hasSubmitted && !hasSomeActive && (
          <Typography sx={styles.globalError}>
            <span>*</span> {t('Questionarie.mustBeSelectSomeEvidence')}
          </Typography>
        )}

        <Box sx={styles.defaultButtonContainer}>
          <Button variant='contained' onClick={handleUseDefault} sx={styles.defaultButton}>
            {t('general.defaultSetting')}
          </Button>
        </Box>
      </Box>
    </ConfigLayout>
  )
}

export default ConfigureSolverEvidences
